import {inject, injectable} from 'inversify'
import {DI} from '@/src/config/di/types'
import type {
  ApiRequestOptions,
  IApiRequestBuilder,
  IEndpointResolver,
  IMayBeNeedProxyResolver,
} from '@/src/ports/services/api'
import type {IClientIpResolver} from '@/src/ports/services/request-context'
import type {StorageAdapter} from '@/src/core/domain/data/storage'
import {csrfHeaderName, mobileTokenHeaderName, settings} from '@/src/config/settings'
import qs from 'qs'
import {Routes} from '@/src/config/routes'
import keys from 'lodash/keys'
import {getClientCredentials} from '@/src/shared/utils/client-credentials'
import {getRunlabClient} from '@/src/shared/utils/functions'

@injectable()
export class ApiRequestBuilder implements IApiRequestBuilder, IMayBeNeedProxyResolver {
  @inject(DI.IEndpointResolver)
  endpointResolver!: IEndpointResolver

  @inject(DI.IClientIpResolver)
  clientIpResolver!: IClientIpResolver

  needProxy(path: string): boolean {
    return typeof window !== 'undefined' && !path.startsWith('/api/')
  }

  resolveParams(url: string, options: ApiRequestOptions) {
    let params = qs.parse(new URL(url).search)

    params = {...options.data?.parameters, ...params, ...options.params}

    return params
  }

  initOptions(options?: ApiRequestOptions): ApiRequestOptions {
    if (!options) {
      options = {}
    }

    if (!options.headers) {
      options.headers = {}
    }

    if (!options.headers['Content-Type'] && !options.body) {
      options.headers['Content-Type'] = 'application/json'
    }

    return options
  }

  async getAnalyticHeaders(passedHeaders: ApiRequestOptions['headers']) {
    if (!passedHeaders) passedHeaders = {}

    const headers = passedHeaders

    if (!headers['X-Runlab-Client']) {
      headers['X-Runlab-Client'] = getRunlabClient()
    }

    if (typeof window !== 'undefined' && window.mindbox) {
      const deviceUUID: string | undefined = await new Promise(resolve => {
        // @ts-ignore
        window.mindbox('helpers.getDeviceUUID', value => {
          resolve(value)
        })
      })

      if (deviceUUID) {
        headers['X-Mindbox-DeviceId'] = deviceUUID
      }
    }

    return headers
  }

  async build(
    url: string,
    options?: ApiRequestOptions,
  ): Promise<{url: string; options: ApiRequestOptions}> {
    options = this.initOptions(options)
    if (options.withoutSession) {
      // Public cache entries must not vary with any incoming request header.
      options.headers = {'Content-Type': 'application/json'}
      options.credentials = 'omit'
    }

    let endpoint = this.endpointResolver.resolve(url)

    const params = this.resolveParams(endpoint, options)

    if (!this.needProxy(url) && typeof window === 'undefined') {
      options.headers!['X-API-Key'] = settings.NEXT_WEBAPI_TOKEN
      const userIp = options.withoutSession ? 'unknown' : await this.clientIpResolver.resolve()

      if (userIp !== 'unknown') {
        options.headers!['X-Forwarded-For'] = userIp
        options.headers!['X-Real-Ip'] = userIp
      }
    }

    if (this.needProxy(url)) {
      endpoint = `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.api.data.getData}`

      options.data = {...options.data, url, method: options.method, parameters: params}
      options.method = 'POST'
    }

    if (keys(params).length > 0) {
      endpoint = `${endpoint}?${qs.stringify(params)}`
    }

    if (!options.headers) {
      options.headers = {}
    }

    const credentialsHeaders = options.withoutSession ? {} : await getClientCredentials()
    const analyticHeaders = options.withoutSession ? {} : await this.getAnalyticHeaders(options.headers)

    options.headers = {...credentialsHeaders, ...analyticHeaders, ...options.headers}

    options.redirect = 'manual'

    if (options.method?.toLowerCase() === 'post' && !options.data) {
      options.data = {}
    }
    return {url: endpoint, options}
  }
}

@injectable()
export class MobileApiRequestBuilder extends ApiRequestBuilder {
  @inject(DI.IEndpointResolver)
  declare endpointResolver: IEndpointResolver

  @inject(DI.StorageAdapter)
  storage!: StorageAdapter

  async build(
    url: string,
    options?: ApiRequestOptions,
  ): Promise<{url: string; options: ApiRequestOptions}> {
    options = this.initOptions(options)
    if (options.withoutSession) return super.build(url, options)

    const session = await this.storage.getItem<string>('session')
    const mobileCsrf = await this.storage.getItem<string>('mobileCsrf')

    if (session) {
      options!.headers!['X-Auth'] = session
    }

    if (mobileCsrf) {
      options!.headers![csrfHeaderName] = mobileCsrf
    }

    options!.headers![mobileTokenHeaderName] = settings.NEXT_PUBLIC_MOBILE_TOKEN

    if (!options.params) {
      options.params = {}
    }

    options!.headers!['X-Mobile'] = 'true'

    return super.build(url, options)
  }
}
